import { useCallback, useEffect, useMemo, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Plus, Trash2, Save, MessageSquareQuote,
  CheckCircle2, Circle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { adminApi, useAdminAuth } from "../lib/adminAuth";

const PLACEHOLDERS = ["{medication}", "{ingredient}", "{product}", "{section}"];

function Row({ tpl, severities, onSaved, onDeleted, canReview }) {
  const [question, setQuestion] = useState(tpl.question);
  const [severity, setSeverity] = useState(tpl.severity);
  const [section, setSection] = useState(tpl.source_section || "");
  const [approved, setApproved] = useState(tpl.approved);
  const [busy, setBusy] = useState("");

  const dirty =
    question !== tpl.question ||
    severity !== tpl.severity ||
    (section || "") !== (tpl.source_section || "");

  async function save(nextApproved = approved) {
    setBusy("save");
    try {
      const { data } = await adminApi().put(`/admin/report-templates/${tpl.id}`, {
        question, severity, source_section: section || null, approved: nextApproved,
      });
      setApproved(data.approved);
      onSaved(data);
      toast.success(nextApproved && !tpl.approved ? "Approved — now live in reports" : "Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  }

  async function toggleApprove() {
    if (dirty) { await save(!approved); return; }
    setBusy("approve");
    try {
      const { data } = await adminApi().put(`/admin/report-templates/${tpl.id}`, {
        approved: !approved,
      });
      setApproved(data.approved);
      onSaved(data);
      toast.success(data.approved ? "Approved — now live in reports" : "Unapproved — hidden from reports");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(""); }
  }

  async function remove() {
    if (!window.confirm("Delete this template?")) return;
    setBusy("delete");
    try {
      await adminApi().delete(`/admin/report-templates/${tpl.id}`);
      onDeleted(tpl.id);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setBusy("");
    }
  }

  return (
    <div
      data-testid={`template-row-${tpl.id}`}
      className={`rounded-xl border p-4 ${approved ? "border-emerald-300 bg-emerald-50/40" : "border-stone-200 bg-white"}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger data-testid={`template-severity-${tpl.id}`} className="h-8 w-52 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {severities.map((s) => (
              <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          data-testid={`template-section-${tpl.id}`}
          value={section}
          onChange={(e) => setSection(e.target.value)}
          placeholder="Source section (optional)"
          className="h-8 w-56 text-xs"
        />
        <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${approved ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-500"}`}>
          {approved ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
          {approved ? "Live" : "Draft"}
        </span>
      </div>

      <Textarea
        data-testid={`template-question-${tpl.id}`}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        rows={2}
        className="mt-3 text-sm"
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!canReview && (
          <span
            data-testid={`template-clinician-only-${tpl.id}`}
            className="text-[11px] font-semibold uppercase tracking-wider text-stone-500"
          >
            Clinical reviewer only
          </span>
        )}
        <Button
          data-testid={`template-approve-${tpl.id}`}
          size="sm"
          variant={approved ? "outline" : "default"}
          onClick={toggleApprove}
          disabled={!!busy || !canReview}
          title={!canReview ? "Clinical reviewer only." : undefined}
        >
          {busy === "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> :
            approved ? "Unapprove" : "Approve"}
        </Button>
        <Button
          data-testid={`template-save-${tpl.id}`}
          size="sm"
          variant="outline"
          onClick={() => save()}
          disabled={!!busy || !dirty}
        >
          {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          <span className="ml-1">Save</span>
        </Button>
        <Button
          data-testid={`template-delete-${tpl.id}`}
          size="sm"
          variant="ghost"
          className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={remove}
          disabled={!!busy}
        >
          {busy === "delete" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}

function AddForm({ severities, onCreated }) {
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState(severities[0]?.value || "CAUTION");
  const [section, setSection] = useState("");
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);

  async function create() {
    if (question.trim().length < 5) { toast.error("Write a question first."); return; }
    setBusy(true);
    try {
      const { data } = await adminApi().post("/admin/report-templates", {
        severity, source_section: section || null, question, approved: false,
      });
      onCreated(data);
      setQuestion(""); setSection(""); setOpen(false);
      toast.success("Draft added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <Button data-testid="add-template-btn" variant="outline" onClick={() => setOpen(true)} className="w-full">
        <Plus className="h-4 w-4" /> <span className="ml-1">Add a template</span>
      </Button>
    );
  }
  return (
    <div data-testid="add-template-form" className="rounded-xl border border-dashed border-stone-300 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="h-8 w-52 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {severities.map((s) => (
              <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input value={section} onChange={(e) => setSection(e.target.value)}
          placeholder="Source section (optional)" className="h-8 w-56 text-xs" />
      </div>
      <Textarea data-testid="add-template-question" value={question}
        onChange={(e) => setQuestion(e.target.value)} rows={2} className="mt-3 text-sm"
        placeholder="e.g. While I'm on {medication}, is {product} ({ingredient}) still safe?" />
      <div className="mt-3 flex gap-2">
        <Button data-testid="add-template-save" size="sm" onClick={create} disabled={busy}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add draft"}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

export default function AdminReportTemplates() {
  const { admin } = useAdminAuth();
  const canReview = admin?.role === "clinician";
  const [items, setItems] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/report-templates");
      setItems(data.items);
      setSeverities(data.severities);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load templates");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approvedCount = useMemo(() => items.filter((i) => i.approved).length, [items]);

  function onSaved(updated) {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
  }
  function onDeleted(id) {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }
  function onCreated(created) {
    setItems((prev) => [created, ...prev]);
  }

  return (
    <main data-testid="admin-report-templates-page" className="min-h-screen bg-[#fdf8f3] px-6 py-10">
      <div className="mx-auto max-w-3xl">
        <AdminHeader />

        <div className="flex items-center gap-2">
          <MessageSquareQuote className="h-6 w-6 text-stone-700" />
          <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Report Templates</h1>
        </div>
        <p className="mt-1 text-sm text-stone-600">
          These power the “Questions to ask your prescriber” section of the
          Discussion Report. A question appears in a report only once it’s{" "}
          <strong>approved</strong>. Each verified finding picks the approved
          template that matches its severity.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 text-xs text-stone-600">
          <span data-testid="template-count">
            <strong className="text-stone-900">{approvedCount}</strong> of {items.length} approved
          </span>
          <span className="text-stone-300">·</span>
          <span>Placeholders:</span>
          {PLACEHOLDERS.map((p) => (
            <code key={p} className="rounded bg-stone-100 px-1.5 py-0.5 text-[11px] text-stone-700">{p}</code>
          ))}
        </div>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>
        ) : (
          <div className="mt-5 space-y-3">
            <AddForm severities={severities} onCreated={onCreated} />
            {items.map((tpl) => (
              <Row key={tpl.id} tpl={tpl} severities={severities}
                onSaved={onSaved} onDeleted={onDeleted} canReview={canReview} />
            ))}
            {items.length === 0 && (
              <p className="py-8 text-center text-sm text-stone-500">No templates yet.</p>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
